import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../atom/Button';
import TitleHeader from '../modecules/TitleHeader';
import AdminBoardService from 'src/service/AdminBoardService';
import {
  SERVICE_TYPE_DISPLAY_MAP,
  BUSINESS_TYPE_MAP,
  FILE_CODE_LABEL_MAP,
  MESSAGE_PURPOSE_OPTIONS,
  COMPANY_TYPE_OPTIONS,
  FILE_CODES_CONFIG,
  getBusinessTypeFromCorpType,
  getStatusDisplay,
  formatDateTime,
  parseVendorType,
  parseMsgPurpose
} from 'src/constants';

// ============================================
// Styled Components
// ============================================
const PageWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px 80px;
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e8eef3;
  border-radius: 8px;
  margin-bottom: 24px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e8eef3;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1a2b3c;
  margin: 0;
`;

const EditButton = styled.button<{ $isEditing?: boolean }>`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  ${props =>
    props.$isEditing
      ? `
      background: #0066cc;
      color: #fff;
      border: 1px solid #0066cc;
      &:hover { background: #0052a3; }
    `
      : `
      background: #fff;
      color: #555;
      border: 1px solid #d1d5db;
      &:hover { background: #f3f4f6; }
    `}
`;

const SectionBody = styled.div`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${props => (props.$fullWidth ? '1 / -1' : 'auto')};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 8px;
`;

const InputField = styled.input<{ $readOnly?: boolean }>`
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 0 14px;
  font-size: 14px;
  border: 1px solid ${props => (props.$readOnly ? '#e8eef3' : '#d1e8fb')};
  border-radius: 6px;
  background-color: ${props => (props.$readOnly ? '#f8fafc' : '#fff')};
  color: ${props => (props.$readOnly ? '#6b7280' : '#1a2b3c')};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => (props.$readOnly ? '#e8eef3' : '#0066cc')};
    box-shadow: ${props => (props.$readOnly ? 'none' : '0 0 0 3px rgba(0, 102, 204, 0.1)')};
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxLabel = styled.label<{ $checked?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${props => (props.$checked ? '#0066cc' : '#e8eef3')};
  border-radius: 6px;
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
  background-color: ${props => (props.$checked ? '#f0f7ff' : '#fff')};
  transition: all 0.2s;

  input[type='checkbox'] {
    position: relative;
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
    accent-color: #0066cc;

    &:checked::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 14px;
      height: 14px;
      background-color: #0066cc;
      border-radius: 2px;
      pointer-events: none;
    }

    &:checked::after {
      content: '✓';
      position: absolute;
      left: 50%;
      top: 48%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 12px;
      font-weight: bold;
      pointer-events: none;
    }
  }
`;

const StatusBadge = styled.span<{ $itemStatus?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 20px;

  ${props => {
    const status = props.$itemStatus || '';
    if (status === '승인') {
      return `
          background-color: #ecfdf5;
          color: #059669;
        `;
    } else if (status === '거절') {
      return `
          background-color: #fef2f2;
          color: #dc2626;
        `;
    }
    return `
        background-color: #fefce8;
        color: #ca8a04;
      `;
  }}
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  margin-top: 24px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const FileLabel = styled.span`
  flex: 0 0 200px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FileName = styled.span`
  flex: 1;
  font-size: 14px;
  color: #0066cc;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0052a3;
  }
`;

const NoFile = styled.span`
  flex: 1;
  font-size: 14px;
  color: #9ca3af;
`;

const FileUploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
  }

  input {
    display: none;
  }
`;

// ============================================
// Types & Constants
// ============================================

// API 응답의 파일 정보
interface FileInfo {
  fileId: number;
  fileCode: string;
  filePath: string;
  originalFilename: string;
  uploadedAt: string;
  deleted: boolean;
  deletedAt: string | null;
}

// 메시징 상세 정보
interface MessagingDetail {
  requestId: number;
  serviceRequest: unknown;
  msgPurpose: string;
  senderType: string;
  agentServerIp: string;
  webhookUrl: string;
  webhookIp: string;
  webhookPort: number;
  createdAt: string;
  updatedAt: string;
}

// 웹콜 상세 정보
interface WebcallDetail {
  requestId: number;
  serviceRequest: unknown;
  companyNature: string;
  sdkDomain: string;
  createdAt: string;
  updatedAt: string;
}

// API 응답 데이터 구조
interface ServiceDetailData {
  requestId: number;
  requestType: string; // 'messaging' | 'aicc'
  serviceKind: string;
  corpType: string; // 'messaging-general', 'messaging-special', 'aicc-general', 'aicc-reseller'
  status: string; // 'pending', 'success', 'fail', etc.
  corporateName: string;
  bizRegNo: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  deptName: string;
  jobTitle: string;
  writerName: string;
  title: string;
  vendorType: string;
  createdAt: string;
  updatedAt: string;
  webcallDetail: WebcallDetail | null;
  messagingDetail: MessagingDetail | null;
  files: FileInfo[];
  // 추가 필드 (UI에서 사용)
  zipCode?: string;
  address?: string;
  addressDetail?: string;
  industry?: string;
  purposeOther?: string;
  companyTypeOther?: string;
  msgPurposes?: string[];
  vendorTypes?: string[];
}

const EMPTY_ITEM: ServiceDetailData = {
  requestId: 0,
  requestType: '',
  serviceKind: '',
  corpType: '',
  status: '',
  corporateName: '',
  bizRegNo: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  deptName: '',
  jobTitle: '',
  writerName: '',
  title: '',
  vendorType: '',
  createdAt: '',
  updatedAt: '',
  webcallDetail: null,
  messagingDetail: null,
  files: [],
  zipCode: '',
  address: '',
  addressDetail: '',
  industry: '',
  purposeOther: '',
  companyTypeOther: ''
};

// ============================================
// Component
// ============================================
const AdminServiceDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [item, setItem] = useState<ServiceDetailData>(location.state || EMPTY_ITEM);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<ServiceDetailData>>({});
  const [newFiles, setNewFiles] = useState<Record<string, File>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    const requestId = searchParams.get('requestId');

    if (requestId) {
      const fetchServiceDetail = async () => {
        setLoading(true);
        try {
          const response = await AdminBoardService.getServiceRequestDetail(Number(requestId));
          if (response.status === 'success' && 'data' in response && response.data) {
            setItem(response.data);
          } else {
            alert('서비스 신청 정보를 불러오는데 실패했습니다.');
            navigate('/AdminVocBoard');
          }
        } catch (error) {
          console.error('서비스 신청 정보 조회 오류:', error);
          alert('서비스 신청 정보를 불러오는데 실패했습니다.');
          navigate('/AdminVocBoard');
        } finally {
          setLoading(false);
        }
      };

      fetchServiceDetail();
    } else if (location.state) {
      console.debug('Admin service detail page data:', location.state);
      setItem(location.state as ServiceDetailData);
    } else {
      console.debug('No initial requestId or location state provided.');
    }
  }, [searchParams, location, navigate]);

  // 현재 서비스 타입과 사업자 유형에 따른 파일 코드 목록 가져오기
  const getCurrentFileCodes = (): string[] => {
    const requestType = item.requestType || 'messaging';
    const businessType = getBusinessTypeFromCorpType(item.corpType);
    return FILE_CODES_CONFIG[requestType]?.[businessType] || [];
  };

  // files 배열에서 fileCode로 파일 정보 찾기
  const getFileByCode = (fileCode: string): FileInfo | undefined => {
    return item.files?.find(f => f.fileCode === fileCode && !f.deleted);
  };

  const handleEditStart = () => {
    setIsEditing(true);
    const purposes = item.messagingDetail ? parseMsgPurpose(item.messagingDetail.msgPurpose) : [];
    const companyTypes = parseVendorType(item.vendorType);
    setEditData({
      ...item,
      msgPurposes: purposes,
      vendorTypes: companyTypes
    });
    setNewFiles({});
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditData({});
    setNewFiles({});
  };

  const handleEditChange = (field: string, value: string | string[]) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxToggle = (field: 'msgPurposes' | 'vendorTypes', value: string) => {
    setEditData(prev => {
      const currentArray = (prev[field] as string[]) || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const handleFileChange = (fileCode: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        alert('파일 크기는 20MB 이내여야 합니다.');
        e.target.value = '';
        return;
      }
      setNewFiles(prev => ({ ...prev, [fileCode]: file }));
    }
  };

  const handleEditSave = async () => {
    try {
      setLoading(true);

      // 수정할 데이터 구성 - editData가 있으면 editData 값 사용, 없으면 item 값 유지
      const dataToSend: Record<string, unknown> = {
        contactName: editData.contactName ?? item.contactName,
        deptName: editData.deptName ?? item.deptName,
        jobTitle: editData.jobTitle ?? item.jobTitle,
        contactPhone: editData.contactPhone ?? item.contactPhone,
        corporateName: editData.corporateName ?? item.corporateName,
        industry: editData.industry ?? item.industry ?? '',
        bizRegNo: editData.bizRegNo ?? item.bizRegNo,
        contactEmail: editData.contactEmail ?? item.contactEmail,
        zipCode: editData.zipCode ?? item.zipCode ?? '',
        address: editData.address ?? item.address ?? '',
        addressDetail: editData.addressDetail ?? item.addressDetail ?? '',
        purposeOther: editData.purposeOther ?? item.purposeOther ?? '',
        companyTypeOther: editData.companyTypeOther ?? item.companyTypeOther ?? '',
        msgPurpose:
          editData.msgPurposes ??
          (item.messagingDetail ? parseMsgPurpose(item.messagingDetail.msgPurpose) : []),
        vendorType: editData.vendorTypes ?? parseVendorType(item.vendorType),
        agentServerIp:
          editData.messagingDetail?.agentServerIp ?? item.messagingDetail?.agentServerIp ?? '',
        webhookUrl: editData.messagingDetail?.webhookUrl ?? item.messagingDetail?.webhookUrl ?? '',
        sdkDomain: editData.webcallDetail?.sdkDomain ?? item.webcallDetail?.sdkDomain ?? '',
        corpType: item.corpType,
        status: item.status,
        writerName: item.writerName,
        title: item.title,
        serviceKind: item.serviceKind
      };

      const response = await AdminBoardService.updateServiceRequest(
        item.requestId,
        dataToSend,
        newFiles,
        item.requestType
      );

      if (response.status === 'success') {
        // 성공 시 서버에서 최신 데이터 다시 불러오기
        try {
          const refreshResponse = await AdminBoardService.getServiceRequestDetail(item.requestId);
          if (
            refreshResponse.status === 'success' &&
            'data' in refreshResponse &&
            refreshResponse.data
          ) {
            setItem(refreshResponse.data);
          } else if (response.data) {
            setItem(response.data as ServiceDetailData);
          }
        } catch {
          // 새로고침 실패 시 응답 데이터 또는 기존 데이터 유지
          if (response.data) {
            setItem(response.data as ServiceDetailData);
          }
        }
        setIsEditing(false);
        setEditData({});
        setNewFiles({});
        alert('수정되었습니다.');
      } else {
        alert('수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('수정 중 오류:', error);
      alert('수정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (newStatus: string) => {
    try {
      setLoading(true);
      const response = await AdminBoardService.updateServiceStatus(item.requestId, newStatus);
      if (response.status === 'success') {
        alert(
          newStatus === 'success'
            ? '승인되었습니다.'
            : newStatus === 'fail'
              ? '거절되었습니다.'
              : '처리되었습니다.'
        );
        navigate('/AdminVocBoard');
      } else {
        alert('처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('상태 변경 중 오류:', error);
      alert('처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (requestId: number, fileId: number, fileName: string) => {
    await AdminBoardService.serviceFileDownload(requestId, fileId, fileName);
  };

  const businessType = getBusinessTypeFromCorpType(item.corpType);
  const showMessagingFields = item.requestType === 'messaging';
  const showAiccFields = item.requestType === 'aicc';

  // 메시징 상세에서 purposes, vendorType 파싱
  const purposes = item.messagingDetail ? parseMsgPurpose(item.messagingDetail.msgPurpose) : [];
  const companyTypes = parseVendorType(item.vendorType);

  return (
    <PageWrapper>
      <TitleHeader
        title={'서비스 신청 상세'}
        subTitle={'관리자용 서비스 신청 상세내역입니다. 내용을 확인하고 승인 처리해 주세요.'}
      />

      {/* 상태 표시 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            신청번호: <strong style={{ color: '#0066cc' }}>{item.requestId}</strong>
          </span>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            신청일시: {formatDateTime(item.createdAt)}
          </span>
        </div>
        <StatusBadge $itemStatus={getStatusDisplay(item.status)}>
          {getStatusDisplay(item.status)}
        </StatusBadge>
      </div>

      {/* 통합 수정 섹션 */}
      <Section>
        <SectionHeader>
          <SectionTitle>신청 정보</SectionTitle>
          {!isEditing && <EditButton onClick={handleEditStart}>수정</EditButton>}
        </SectionHeader>
        <SectionBody>
          {/* 고객사 정보 */}
          <SectionTitle style={{ fontSize: '16px', marginBottom: '16px' }}>
            고객사 정보
          </SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>담당자</Label>
              <InputField
                value={(isEditing ? editData.contactName : item.contactName) || ''}
                onChange={e => handleEditChange('contactName', e.target.value)}
                readOnly={!isEditing}
                $readOnly={!isEditing}
              />
            </FormGroup>
            <FormGroup>
              <Label>연락처</Label>
              <InputField
                value={(isEditing ? editData.contactPhone : item.contactPhone) || ''}
                onChange={e => handleEditChange('contactPhone', e.target.value)}
                readOnly={!isEditing}
                $readOnly={!isEditing}
              />
            </FormGroup>
            <FormGroup>
              <Label>부서명</Label>
              <InputField
                value={(isEditing ? editData.deptName : item.deptName) || ''}
                onChange={e => handleEditChange('deptName', e.target.value)}
                readOnly={!isEditing}
                $readOnly={!isEditing}
              />
            </FormGroup>
            <FormGroup>
              <Label>직책</Label>
              <InputField
                value={(isEditing ? editData.jobTitle : item.jobTitle) || ''}
                onChange={e => handleEditChange('jobTitle', e.target.value)}
                readOnly={!isEditing}
                $readOnly={!isEditing}
              />
            </FormGroup>
            <FormGroup>
              <Label>법인명</Label>
              <InputField
                value={(isEditing ? editData.corporateName : item.corporateName) || ''}
                onChange={e => handleEditChange('corporateName', e.target.value)}
                readOnly={!isEditing}
                $readOnly={!isEditing}
              />
            </FormGroup>
            <FormGroup>
              <Label>사업자등록번호</Label>
              <InputField
                value={(isEditing ? editData.bizRegNo : item.bizRegNo) || ''}
                onChange={e => handleEditChange('bizRegNo', e.target.value)}
                readOnly={!isEditing}
                $readOnly={!isEditing}
              />
            </FormGroup>
            <FormGroup $fullWidth>
              <Label>담당자 이메일</Label>
              <InputField
                value={(isEditing ? editData.contactEmail : item.contactEmail) || ''}
                onChange={e => handleEditChange('contactEmail', e.target.value)}
                readOnly={!isEditing}
                $readOnly={!isEditing}
              />
            </FormGroup>
            <FormGroup $fullWidth>
              <Label>사업장 주소</Label>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                <InputField
                  style={{ width: '120px' }}
                  value={(isEditing ? editData.zipCode : item.zipCode) || ''}
                  onChange={e => handleEditChange('zipCode', e.target.value)}
                  readOnly={!isEditing}
                  $readOnly={!isEditing}
                  placeholder="우편번호"
                />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <InputField
                  style={{ flex: 1 }}
                  value={(isEditing ? editData.address : item.address) || ''}
                  onChange={e => handleEditChange('address', e.target.value)}
                  readOnly={!isEditing}
                  $readOnly={!isEditing}
                  placeholder="도로명 주소"
                />
                <InputField
                  style={{ flex: 1 }}
                  value={(isEditing ? editData.addressDetail : item.addressDetail) || ''}
                  onChange={e => handleEditChange('addressDetail', e.target.value)}
                  readOnly={!isEditing}
                  $readOnly={!isEditing}
                  placeholder="상세 주소"
                />
              </div>
            </FormGroup>
          </FormGrid>

          {/* 서비스 정보 */}
          <SectionTitle style={{ fontSize: '16px', marginTop: '32px', marginBottom: '16px' }}>
            서비스 정보
          </SectionTitle>
          <FormGrid>
            <FormGroup $fullWidth>
              <Label>서비스 종류</Label>
              <InputField
                value={SERVICE_TYPE_DISPLAY_MAP[item.requestType] || item.serviceKind || ''}
                readOnly
                $readOnly
              />
            </FormGroup>
            <FormGroup $fullWidth>
              <Label>사업자 유형</Label>
              <InputField
                value={BUSINESS_TYPE_MAP[businessType] || businessType || ''}
                readOnly
                $readOnly
              />
            </FormGroup>

            {showMessagingFields && (
              <>
                <FormGroup $fullWidth>
                  <Label>메시지 발송 목적</Label>
                  <CheckboxGrid>
                    {MESSAGE_PURPOSE_OPTIONS.map(opt => {
                      const currentPurposes = isEditing
                        ? (editData.msgPurposes as string[]) || []
                        : purposes;
                      const checked = currentPurposes.includes(opt.value);
                      return (
                        <CheckboxLabel key={opt.value} $checked={checked} $disabled={!isEditing}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!isEditing}
                            onChange={() => handleCheckboxToggle('msgPurposes', opt.value)}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span style={{ fontSize: '14px' }}>{opt.label}</span>
                        </CheckboxLabel>
                      );
                    })}
                  </CheckboxGrid>
                  {(isEditing ? (editData.msgPurposes as string[]) || [] : purposes).includes(
                    'other'
                  ) && (
                    <div style={{ marginTop: '12px' }}>
                      <InputField
                        value={(isEditing ? editData.purposeOther : item.purposeOther) || ''}
                        onChange={e => handleEditChange('purposeOther', e.target.value)}
                        readOnly={!isEditing}
                        $readOnly={!isEditing}
                        placeholder="기타 목적을 입력해주세요"
                      />
                    </div>
                  )}
                </FormGroup>

                <FormGroup $fullWidth>
                  <Label>회사 유형</Label>
                  <CheckboxGrid>
                    {COMPANY_TYPE_OPTIONS.map(opt => {
                      const currentTypes = isEditing
                        ? (editData.vendorTypes as string[]) || []
                        : companyTypes;
                      const checked = currentTypes.includes(opt.value);
                      return (
                        <CheckboxLabel key={opt.value} $checked={checked} $disabled={!isEditing}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!isEditing}
                            onChange={() => handleCheckboxToggle('vendorTypes', opt.value)}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span style={{ fontSize: '14px' }}>{opt.label}</span>
                        </CheckboxLabel>
                      );
                    })}
                  </CheckboxGrid>
                  {(isEditing ? (editData.vendorTypes as string[]) || [] : companyTypes).includes(
                    'other'
                  ) && (
                    <div style={{ marginTop: '12px' }}>
                      <InputField
                        value={
                          (isEditing ? editData.companyTypeOther : item.companyTypeOther) || ''
                        }
                        onChange={e => handleEditChange('companyTypeOther', e.target.value)}
                        readOnly={!isEditing}
                        $readOnly={!isEditing}
                        placeholder="기타 회사 유형을 입력해주세요"
                      />
                    </div>
                  )}
                </FormGroup>

                <FormGroup $fullWidth>
                  <Label>Agent 설치 서버 IP</Label>
                  <InputField
                    value={item.messagingDetail?.agentServerIp || ''}
                    readOnly
                    $readOnly
                  />
                </FormGroup>

                <FormGroup $fullWidth>
                  <Label>Webhook URL</Label>
                  <InputField value={item.messagingDetail?.webhookUrl || ''} readOnly $readOnly />
                </FormGroup>
              </>
            )}

            {showAiccFields && (
              <FormGroup $fullWidth>
                <Label>SDK Domain</Label>
                <InputField value={item.webcallDetail?.sdkDomain || ''} readOnly $readOnly />
              </FormGroup>
            )}
          </FormGrid>

          {/* 첨부 서류 */}
          <SectionTitle style={{ fontSize: '16px', marginTop: '32px', marginBottom: '16px' }}>
            첨부 서류
          </SectionTitle>
          <div style={{ border: '1px solid #e8eef3', borderRadius: '6px', padding: '8px 16px' }}>
            {getCurrentFileCodes().map(fileCode => {
              const fileInfo = getFileByCode(fileCode);
              const newFile = newFiles[fileCode];
              const label = FILE_CODE_LABEL_MAP[fileCode] || fileCode;

              return (
                <FileRow key={fileCode}>
                  <FileLabel>{label}</FileLabel>
                  {newFile ? (
                    <FileName>{newFile.name}</FileName>
                  ) : fileInfo ? (
                    <FileName
                      onClick={() =>
                        downloadFile(item.requestId, fileInfo.fileId, fileInfo.originalFilename)
                      }
                    >
                      {fileInfo.originalFilename}
                    </FileName>
                  ) : (
                    <NoFile>첨부파일 없음</NoFile>
                  )}
                  {isEditing && (
                    <FileUploadButton>
                      파일 선택
                      <input
                        type="file"
                        ref={el => {
                          fileInputRefs.current[fileCode] = el;
                        }}
                        onChange={e => handleFileChange(fileCode, e)}
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.hwp,.doc,.docx"
                      />
                    </FileUploadButton>
                  )}
                </FileRow>
              );
            })}
            {getCurrentFileCodes().length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                첨부된 서류가 없습니다.
              </div>
            )}
          </div>
        </SectionBody>
      </Section>

      {/* 하단 액션 버튼 */}
      <ActionBar>
        <Button
          onClick={() => navigate(-1)}
          text={'목록'}
          backgroundColor={'#fff'}
          fontColor={'#555'}
          border={'1px solid #d1d5db'}
          borderRadius={'6px'}
        />
        <ActionButtons>
          {isEditing ? (
            // 수정 모드일 때: 취소/저장 버튼
            <>
              <Button
                onClick={handleEditCancel}
                text={'취소'}
                backgroundColor={'#fff'}
                fontColor={'#555'}
                border={'1px solid #d1d5db'}
                borderRadius={'6px'}
                width={'100px'}
              />
              <Button
                onClick={handleEditSave}
                text={'저장'}
                backgroundColor={'#059669'}
                fontColor={'white'}
                borderRadius={'6px'}
                width={'120px'}
              />
            </>
          ) : (
            // 일반 모드일 때: 승인/거절 버튼
            (() => {
              const statusLower = (item.status || '').toLowerCase();

              if (['success', 'approved'].includes(statusLower)) {
                return null;
              }

              if (['fail', 'rejected'].includes(statusLower)) {
                return (
                  <Button
                    onClick={() => handleApprove('success')}
                    text={'재승인'}
                    backgroundColor={'#0066cc'}
                    fontColor={'white'}
                    borderRadius={'6px'}
                    width={'120px'}
                  />
                );
              }

              return (
                <>
                  <Button
                    onClick={() => handleApprove('fail')}
                    text={'거절'}
                    backgroundColor={'#fff'}
                    fontColor={'#dc2626'}
                    border={'1px solid #dc2626'}
                    borderRadius={'6px'}
                    width={'100px'}
                  />
                  <Button
                    onClick={() => handleApprove('success')}
                    text={'승인'}
                    backgroundColor={'#0066cc'}
                    fontColor={'white'}
                    borderRadius={'6px'}
                    width={'120px'}
                  />
                </>
              );
            })()
          )}
        </ActionButtons>
      </ActionBar>
    </PageWrapper>
  );
};

export default AdminServiceDetailPage;
